import React, { useState, useEffect } from 'react';
import { Star, Buy, Heart } from 'react-iconly';

export function ProductInfoDrawer({ 
  isOpen, 
  onClose, 
  product,
  onAddToCart,
  onToggleFavorite,
  isFavorite = false,
  cartQuantity = 0
}) {
  const [quantity, setQuantity] = useState(1);
  const [variationSelections, setVariationSelections] = useState({});
  const [selectedFulfillmentType, setSelectedFulfillmentType] = useState('pickup');
  const [selectedPickupDate, setSelectedPickupDate] = useState('');
  const [selectedReadyTime, setSelectedReadyTime] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [availableDates, setAvailableDates] = useState([]);

  // Detect mobile vs desktop and track window width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      // Consider mobile if width is <= 1024px, but also check for bottom nav presence
      const hasBottomNav = document.querySelector('nav[role="navigation"][aria-label="Bottom navigation"]');
      setIsMobile(width <= 1024 || !!hasBottomNav);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    // Also check when DOM updates (in case nav loads after component)
    const observer = new MutationObserver(handleResize);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, []);

  // Calculate available dates based on product inventory
  useEffect(() => {
    if (product?.inventory?.scheduled && selectedFulfillmentType === 'pickup') {
      const today = new Date();
      const todayString = today.toISOString().split('T')[0];
      
      console.log('[ProductInfoDrawer] Calculating available dates:', {
        productName: product?.name,
        today: todayString,
        quantity: quantity,
        policy: product?.policy,
        allScheduledDates: product.inventory.scheduled.map(row => ({
          date: row.service_date,
          available: row.available_total
        }))
      });
      
      const availableDatesList = product.inventory.scheduled
        .filter(row => {
          console.log('[ProductInfoDrawer] Checking date:', row.service_date, {
            enabled: row.enabled,
            available_total: row.available_total,
            quantity_needed: quantity,
            is_future: row.service_date > todayString
          });
          
          // Check if this date slot is enabled
          if (!row.enabled) {
            console.log('[ProductInfoDrawer] Date filtered out: not enabled');
            return false;
          }
          
          // Check if date has available units
          if (row.available_total < quantity) {
            console.log('[ProductInfoDrawer] Date filtered out: insufficient units');
            return false;
          }
          
          // Check if date is in the future
          if (row.service_date <= todayString) {
            console.log('[ProductInfoDrawer] Date filtered out: not in future');
            return false;
          }
          
          // Check policy restrictions
          const policy = product?.policy;
          if (policy) {
            // Parse date string directly to avoid timezone issues
            const [year, month, day] = row.service_date.split('-').map(Number);
            const orderDate = new Date(year, month - 1, day, 12, 0, 0, 0);
            const daysDiff = Math.ceil((orderDate - today) / (1000 * 60 * 60 * 24));
            
            // Check if date is within policy start/end date range
            if (policy.start_date && row.service_date < policy.start_date) {
              console.log('[ProductInfoDrawer] Date filtered out: before policy start_date');
              return false;
            }
            
            if (policy.end_date && row.service_date > policy.end_date) {
              console.log('[ProductInfoDrawer] Date filtered out: after policy end_date');
              return false;
            }
            
            console.log('[ProductInfoDrawer] Policy check:', {
              daysDiff,
              minOrderAhead: policy.min_order_ahead?.value,
              sameDayAllowed: policy.same_day_allowed,
              startDate: policy.start_date,
              endDate: policy.end_date
            });
            
            // Check min_order_ahead policy
            if (policy.min_order_ahead && policy.min_order_ahead.value !== undefined) {
              const minDays = policy.min_order_ahead.value;
              if (minDays > 0 && daysDiff < minDays) {
                console.log('[ProductInfoDrawer] Date filtered out: too soon (min_order_ahead)');
                return false;
              }
            }
            
            // Check same_day_allowed policy
            if (!policy.same_day_allowed && daysDiff === 0) {
              console.log('[ProductInfoDrawer] Date filtered out: same day not allowed');
              return false;
            }
          }
          
          console.log('[ProductInfoDrawer] Date passed all checks:', row.service_date);
          return true;
        })
        .map(row => row.service_date)
        .sort();
      
      console.log('[ProductInfoDrawer] Final available dates:', availableDatesList);
      setAvailableDates(availableDatesList);
      
      // Reset selected date if it's no longer available
      if (selectedPickupDate && !availableDatesList.includes(selectedPickupDate)) {
        setSelectedPickupDate('');
        setSelectedReadyTime('');
      }
    } else {
      setAvailableDates([]);
    }
  }, [product, quantity, selectedFulfillmentType, selectedPickupDate]);

  // Reset state when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      setVariationSelections({});
      setSelectedFulfillmentType('pickup');
      setSelectedPickupDate('');
      setSelectedReadyTime('');
    }
  }, [isOpen]);

  if (!isOpen || !product) return null;

  console.log('[ProductInfoDrawer] Rendering drawer for product:', product?.name);
  console.log('[ProductInfoDrawer] Product data:', product);

  // Check if all required variations are selected
  const allVariationsSelected = (product) => {
    if (!product?.variants || product.variants.length === 0) return true;
    
    return product.variants.every(variant => {
      return variationSelections[variant.name] !== undefined;
    });
  };

  // Handle add to cart
  const handleAddToCart = () => {
    if (allVariationsSelected(product)) {
      console.log('[ProductInfoDrawer] Adding to cart:', { 
        product, 
        quantity, 
        variationSelections, 
        fulfillmentType: selectedFulfillmentType,
        pickupDate: selectedPickupDate,
        readyTime: selectedReadyTime
      });
      if (onAddToCart) {
        onAddToCart(product, quantity, variationSelections, {
          fulfillmentType: selectedFulfillmentType,
          pickupDate: selectedPickupDate,
          readyTime: selectedReadyTime
        });
      }
    }
  };

  // Handle favorite toggle
  const handleToggleFavorite = () => {
    console.log('[ProductInfoDrawer] Toggling favorite:', product);
    if (onToggleFavorite) {
      onToggleFavorite(product);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div 
        className="product-info-drawer"
        style={{
          background: 'rgba(255, 255, 255, 0.75)',
          backdropFilter: 'blur(12px)',
          width: '100%',
          // Position drawer above bottom nav using absolute positioning
          position: 'absolute',
          // Check if we need to account for bottom nav - use responsive check based on width
          // Bottom nav visible height is ~60-70px (including padding), use 72px for safety
          bottom: (windowWidth <= 1024 || isMobile) 
            ? 'calc(72px + env(safe-area-inset-bottom))' // Mobile/tablet: position above bottom nav (72px) + safe area
            : '0', // Desktop: bottom of screen
          left: 0,
          right: 0,
          // Account for bottom navigation bar - use responsive check
          maxHeight: (windowWidth <= 1024 || isMobile)
            ? 'calc(100vh - 72px - env(safe-area-inset-bottom))' // Mobile/tablet: reserve space for bottom nav
            : '85vh', // Desktop: normal max height
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          paddingTop: '32px',
          paddingLeft: '20px',
          paddingRight: '20px',
          // Bottom padding for button spacing - ensure button is visible above nav
          paddingBottom: (windowWidth <= 1024 || isMobile)
            ? 'calc(48px + env(safe-area-inset-bottom))' // Mobile/tablet: extra padding to ensure button visibility
            : '32px', // Desktop: normal padding
          overflowY: 'auto',
          overflowX: 'hidden',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          animation: 'slideUpSheet 0.3s cubic-bezier(.4,1.6,.6,1)',
          // Ensure smooth scrolling on mobile
          WebkitOverflowScrolling: 'touch',
          // Higher z-index than bottom nav (1000) to ensure it's above
          zIndex: 1001
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag Handle */}
        <div style={{ 
          width: 40, 
          height: 4, 
          background: '#eee', 
          borderRadius: 2, 
          alignSelf: 'center', 
          marginBottom: 12 
        }} />

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 8
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ 
              fontFamily: 'Sora, sans-serif',
              fontWeight: 700, 
              fontSize: 20, 
              margin: 0,
              color: '#222'
            }}>
              {product?.name || 'Product Details'}
            </h2>
            <div style={{ 
              background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
              backgroundSize: '200% 200%',
              animation: 'gradientShift 3s ease infinite',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontWeight: 600, 
              fontSize: 16,
              marginTop: 4
            }}>
              ${product?.price || '0.00'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#666',
              padding: 4,
              marginLeft: 16
            }}
          >
            ×
          </button>
        </div>

        {/* Allergens */}
        <div>
          <div style={{ 
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 600, 
            fontSize: 13, 
            marginBottom: 4,
            color: '#333'
          }}>
            Allergens
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {product?.allergens && product.allergens.length > 0 ? (
              product.allergens.map((allergen, i) => (
                <span key={i} style={{ 
                  background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                  backgroundSize: '200% 200%',
                  animation: 'gradientShift 3s ease infinite',
                  color: '#fff', 
                  borderRadius: 8, 
                  padding: '4px 10px', 
                  fontSize: 12,
                  fontWeight: 500
                }}>
                  {allergen}
                </span>
              ))
            ) : (
              <span style={{ 
                color: '#666', 
                fontSize: 12,
                fontStyle: 'italic'
              }}>
                No allergens found
              </span>
            )}
          </div>
        </div>

        {/* Description */}
        {product?.description && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 4,
              color: '#333'
            }}>
              Description
            </div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 13, 
              color: '#444',
              lineHeight: 1.5
            }}>
              {product.description}
            </div>
          </div>
        )}

        {/* Dietary Tags */}
        {product?.dietary && product.dietary.length > 0 && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 8, 
              color: '#333' 
            }}>
              Dietary
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {product.dietary.map((tag, i) => (
                <span key={i} style={{ 
                  background: '#E8F5E8',
                  color: '#2E7D32', 
                  borderRadius: 8, 
                  padding: '4px 10px', 
                  fontSize: 12,
                  fontWeight: 500
                }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Cuisine */}
        {product?.cuisine && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 8, 
              color: '#333' 
            }}>
              Cuisine
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ 
                background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite',
                color: '#fff', 
                borderRadius: 8, 
                padding: '4px 10px', 
                fontSize: 12,
                fontWeight: 500
              }}>
                {product.cuisine}
              </span>
            </div>
          </div>
        )}

        {/* Dish Type */}
        {product?.dishType && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 8, 
              color: '#333' 
            }}>
              Dish Type
            </div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontSize: 13, 
              color: '#444',
              background: '#F5F5F5',
              padding: '8px 12px',
              borderRadius: 8
            }}>
              {product.dishType}
            </div>
          </div>
        )}

        {/* Course Type */}
        {product?.courseType && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 8, 
              color: '#333' 
            }}>
              Course Type
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ 
                background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite',
                color: '#fff', 
                borderRadius: 8, 
                padding: '4px 10px', 
                fontSize: 12,
                fontWeight: 500
              }}>
                {product.courseType}
              </span>
            </div>
          </div>
        )}

        {/* Fulfillment Type Selection */}
        {(product?.acceptsPickup || product?.acceptsDelivery) && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 12, 
              marginBottom: 6, 
              color: '#333' 
            }}>
              Fulfillment Method
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {product.acceptsPickup && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#333',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="fulfillment"
                    value="pickup"
                    checked={selectedFulfillmentType === 'pickup'}
                    onChange={(e) => setSelectedFulfillmentType(e.target.value)}
                    style={{
                      marginRight: 10,
                      accentColor: '#e76f51',
                      transform: 'scale(1.2)'
                    }}
                  />
                  Pickup
                </label>
              )}
              {product.acceptsDelivery && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#333',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="fulfillment"
                    value="delivery"
                    checked={selectedFulfillmentType === 'delivery'}
                    onChange={(e) => setSelectedFulfillmentType(e.target.value)}
                    style={{
                      marginRight: 10,
                      accentColor: '#e76f51',
                      transform: 'scale(1.2)'
                    }}
                  />
                  Delivery
                </label>
              )}
            </div>
          </div>
        )}

        {/* Pickup Date Selection */}
        {selectedFulfillmentType === 'pickup' && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 12, 
              marginBottom: 6, 
              color: '#333' 
            }}>
              Pickup Date
            </div>
            {availableDates.length > 0 ? (
              <select
                value={selectedPickupDate}
                onChange={(e) => {
                  setSelectedPickupDate(e.target.value);
                  setSelectedReadyTime(''); // Reset ready time when date changes
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 13,
                  fontFamily: 'Montserrat, sans-serif',
                  backgroundColor: '#fff',
                  color: '#333',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#e76f51'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              >
                <option value="">Select pickup date</option>
                {availableDates.map(date => {
                  // Parse date string directly to avoid timezone issues
                  const [year, month, day] = date.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day, 12, 0, 0, 0);
                  const formattedDate = dateObj.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  });
                  return (
                    <option key={date} value={date}>
                      {formattedDate}
                    </option>
                  );
                })}
              </select>
            ) : (
              <div style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                fontSize: 13,
                fontFamily: 'Montserrat, sans-serif',
                backgroundColor: '#f5f5f5',
                color: '#999',
                textAlign: 'center'
              }}>
                No available dates
              </div>
            )}
          </div>
        )}

        {/* Ready Time Selection */}
        {selectedFulfillmentType === 'pickup' && selectedPickupDate && (() => {
          // Get the scheduled date entry for the selected pickup date
          const scheduledDate = product?.inventory?.scheduled?.find(
            s => s.service_date === selectedPickupDate
          );
          
          // Get time range from scheduled date, or use defaults
          const startTime = scheduledDate?.start_time_local || '08:00';
          const endTime = scheduledDate?.end_time_local || '18:00';
          
          // Parse start and end times (format: "HH:MM")
          const [startHour, startMin] = startTime.split(':').map(Number);
          const [endHour, endMin] = endTime.split(':').map(Number);
          
          const startMinutes = startHour * 60 + startMin;
          const endMinutes = endHour * 60 + endMin;
          
          // Generate time slots in 30-minute increments within the range
          const generateTimeSlots = () => {
            const slots = [];
            let currentMinutes = startMinutes;
            
            while (currentMinutes <= endMinutes) {
              const hours = Math.floor(currentMinutes / 60);
              const minutes = currentMinutes % 60;
              const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
              
              // Format for display (12-hour format with AM/PM)
              const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
              const ampm = hours >= 12 ? 'PM' : 'AM';
              const displayMin = minutes === 0 ? '' : `:${String(minutes).padStart(2, '0')}`;
              const displayTime = `${displayHour}${displayMin} ${ampm}`;
              
              slots.push({ value: timeString, label: displayTime });
              currentMinutes += 30; // 30-minute increments
            }
            
            return slots;
          };
          
          const availableTimeSlots = generateTimeSlots();
          
          return (
            <div>
              <div style={{ 
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 600, 
                fontSize: 12, 
                marginBottom: 6, 
                color: '#333' 
              }}>
                Ready Time
              </div>
              <select
                value={selectedReadyTime}
                onChange={(e) => setSelectedReadyTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ddd',
                  fontSize: 13,
                  fontFamily: 'Montserrat, sans-serif',
                  backgroundColor: '#fff',
                  color: '#333',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#e76f51'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              >
                <option value="">Select ready time</option>
                {availableTimeSlots.map(slot => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
            </div>
          );
        })()}

        {/* Variations */}
        {product?.variants && product.variants.length > 0 && (
          <div>
            <div style={{ 
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 600, 
              fontSize: 13, 
              marginBottom: 8, 
              color: '#333' 
            }}>
              Variations
            </div>
            {product.variants.map((variation, vIdx) => (
              <div key={vIdx} style={{ marginBottom: 12 }}>
                <div style={{ 
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 500, 
                  fontSize: 12, 
                  marginBottom: 4, 
                  color: '#555' 
                }}>
                  {variation.name}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {variation.options.map((option, oIdx) => {
                    const optionValue = typeof option === 'object' ? option.label : option;
                    return (
                      <button
                        key={oIdx}
                        type="button"
                        onClick={() => {
                          setVariationSelections(prev => ({
                            ...prev,
                            [variation.name]: optionValue
                          }));
                        }}
                        style={{
                          padding: '8px 12px',
                          borderRadius: 8,
                          border: variationSelections[variation.name] === optionValue ? '2px solid #e76f51' : '1px solid #ddd',
                          background: variationSelections[variation.name] === optionValue ? '#e76f51' : '#fff',
                          color: variationSelections[variation.name] === optionValue ? '#fff' : '#333',
                          fontSize: 12,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontFamily: 'Montserrat, sans-serif',
                          fontWeight: 500
                        }}
                      >
                        {optionValue}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quantity Selector */}
        <div>
          <div style={{ 
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 600, 
            fontSize: 13, 
            marginBottom: 8, 
            color: '#333' 
          }}>
            Quantity
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              type="button"
              onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
              disabled={quantity <= 1}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #e76f51',
                background: 'white',
                color: quantity <= 1 ? '#ccc' : '#e76f51',
                fontSize: '16px', 
                fontWeight: 'bold', 
                cursor: quantity <= 1 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: quantity <= 1 ? 0.5 : 1
              }}
              aria-label="Decrease quantity"
            >
              -
            </button>
            <span style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              minWidth: '40px',
              textAlign: 'center',
              fontFamily: 'Montserrat, sans-serif'
            }}>
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => setQuantity(prev => prev + 1)}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                border: '2px solid #e76f51',
                background: '#e76f51',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              aria-label="Increase quantity"
            >
              +
            </button>
          </div>
        </div>

        {/* Add to Cart Button - Ensure it's always visible at bottom */}
        <div style={{
          marginTop: 'auto',
          paddingTop: 16,
          paddingBottom: (windowWidth <= 1024 || isMobile) ? '0' : '0', // No extra padding, handled by container
          flexShrink: 0,
          position: 'relative',
          zIndex: 1
        }}>
          <button
            style={{
              width: '100%',
              background: allVariationsSelected(product) 
                ? 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)'
                : '#E5E5E5',
              backgroundSize: allVariationsSelected(product) ? '200% 200%' : 'auto',
              animation: allVariationsSelected(product) ? 'gradientShift 3s ease infinite' : 'none',
              color: allVariationsSelected(product) ? '#fff' : '#aaa',
              border: 'none',
              borderRadius: 12,
              padding: '14px 0',
              fontSize: 14,
              fontWeight: '700',
              cursor: allVariationsSelected(product) ? 'pointer' : 'not-allowed',
              boxShadow: '0 2px 8px #0001',
              transition: 'all 0.2s',
              fontFamily: 'Montserrat, sans-serif',
              minHeight: '48px' // Ensure button has minimum height
            }}
            onClick={handleAddToCart}
            aria-label="Add to cart"
            disabled={!allVariationsSelected(product)}
          >
            Add to Cart {cartQuantity > 0 && `(${cartQuantity})`}
          </button>
        </div>

        {/* CSS Animations */}
        <style>{`
          @keyframes slideUpSheet {
            0% { transform: translateY(100%); }
            100% { transform: translateY(0); }
          }
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
      </div>
    </div>
  );
}